import { UserRoleType } from '../../../projects/client';
import { AnalyticsClientRole } from '../../@store/auth/auth.model';

export interface GTMEvent {
  action: string;
  category: string;
  id?: string;
  label?: string;
}
export interface GTMEventModel {
  destination?: string;
  event: string;
  eventAction?: string;
  eventCategory?: string;
  eventId?: string;
  eventLabel?: string;
  eventValue?: string;
  eventType?: string;
  identityType: string;
  partnerId: string;
  selectedDeviceId: string;
  selectedGatewayId: string;
  roleGroup: UserRoleType;
  userType: AnalyticsClientRole;
  viguideVersion: string;
}

export const GTM_LABELS: { [key: string]: string } = {
  installations: 'Installations #page',
  installationDetails: 'Installation details #page',
  invitations: 'Invitations #page',
  invitationForm: 'New invitation #form',
  profile: 'Profile #page',
  home: 'Home #page',
  menu: 'Menu #sidenav',
  managerPanel: 'Manager panel #sidenav',
  notification: 'Service ticket callback #notification',
  confirmDialog: 'Confirm #dialog',
  map: 'Map #page',
  viessmannApps: 'Viessmann apps #popup',
};

export const GTM_CATEGORIES: { [key: string]: string } = {
  booking: 'Booking',
  initials: 'Initials',
  invitation: 'Invitation',
  invitations: 'Invitations',
  acceptInvitation: 'Accept invitation modal',
  newInvitationForm: 'New invitation form',
  userDetails: 'User details',
  userPersonalDetails: 'User personal details',
  userLanguageDetails: 'User language details',
  userContactDetails: 'User contact details',
  userAddressDetails: 'User address details',
  sidenav: 'Sidenav',
  eventBanner: 'Event Banner',
  bundlingBanner: 'Bundling Banner',
  externalLinks: 'Viessmann external links',
  breadcrumb: 'Breadcrumb',
  notification: 'Notification bar',
  navbar: 'Navbar',
  overview: 'Overview',
  footer: 'Footer',
  header: 'Header',
  map: 'Map',
  updates: 'Updates',
  installersOverview: 'Installers overview',
  installer: 'Installer',
  installationsOverview: 'Installations Overview',
  dashboard: 'Dashboard',
  infoPanel: 'Info panel',
  deviceCard: 'Device card',
  buildingManagerCard: 'Building manager card',
  bookedPackageCard: 'Booked package card',
  controlPanel: 'Control panel',
  notePanel: 'Note panel',
  statusPanel: 'Status panel',
  holidayProgram: 'Holiday program',
  boiler: 'System panel',
  system: 'System panel',
  systemDetails: 'System Details',
  systemDetailsModal: 'System Details Modal',
  bmuSerialModal: 'BMU Serial Modal',
  editSerialNumberModal: 'Edit Serial Number Modal',
  defrosting: 'Defrosting',
  gateway: 'Gateway panel',
  gatewayDetails: 'Gateway Details',
  gatewayAccessList: 'Gateway Access List Modal',
  deregisterInstallation: 'Deregister installation modal',
  dhw: 'Domestic hot water',
  fuelCell: 'Fuel cell',
  heatingCircuit: 'Heating circuit',
  heatingZone: 'Heating zone',
  refrigerantCircuitHistory: 'Refrigerant circuit history Modal',
  heatingCircuitGraph: 'Heating curve graph',
  heatingZoneGraph: 'Heating curve graph',
  waterSchedule: 'Domestic hot water schedule',
  circulationSchedule: 'Circulation pump schedule',
  heatingSchedule: 'Heating schedule',
  ventilation: 'Ventilation',
  ventilationSchedule: 'Ventilation schedule',
  selectDays: 'Select days schedule',
  editSchedule: 'Schedule',
  commissioning: 'Commissioning',
  commissioningOverview: 'Commissioning Overview',
  landingPage: 'Landing page',
  serviceTicket: 'Service Ticket',
  notifications: 'Notifications',
  units: 'Units',
  eventLog: 'Event log',
  switchingTimes: 'Switching times side panel',
  heatingCurve: 'Heating curve side panel',
  heatingZoneHeatingCurve: 'Heating zone heating curve side panel',
  spaceClimateCooling: 'Space Climate Cooling side panel',
  operatingMode: 'Operating mode manager',
  singleRoomControl: 'Single room control panel',
  electricHeater: 'Electric Heater',
  floorHeatingTermostat: 'Floor Heating Termostat panel',
  fhtManager: 'Floor Heating Termostat manager Modal',
  hcManager: 'Heat Curve manager',
  hzHeatingCurveManager: 'Heating Zone heating curve manager',
  sccManager: 'Space Climate Cooling manager',
  srcHeating: 'Single Room Control heating panel',
  notFound: 'Not found page',
  accessForbidden: 'Access forbidden',
  browserSupport: 'Browser support modal',
  legalInformation: 'Legal information',
  documentUpdate: 'Document Update',
  firmwareUpdate: 'Firmware Update',
  gatewayFirmwareUpdate: 'Gateway Firmware Update',
  noData: 'No Data',
  commissioningStart: 'Commissioning - Start',
  barcodeScanner: 'Commissioning - Barcode Scanner',
  commissioningIdentifyCode: 'Commissioning - Identify Code',
  commissioningIdentifyCodeSuccess: 'Commissioning - Identify Code Success',
  commissioningPairingSuccess: 'Commissioning - Pairing Success',
  commissioningTypeCode: 'Commissioning - Type Code',
  addRoomModal: 'Add room modal',
  allAddedRooms: 'All added rooms modal',
  allAddedDevices: 'All added devices modal',
  devicePanel: 'Device panel',
  editRoom: 'Edit room modal',
  manageRoom: 'Manage room modal',
  diagnosticsTokenForm: 'Diagnostics token form',
  diagnosticsModal: 'Diagnostics connection modal',
  diagnosticsDisconnectModal: 'Diagnostics disconnect modal',
  diagnosticsPageHeader: 'Remote diagnostics page header',
  diagnosticsPage: 'Remote diagnostics page',
  diagnosticsTimePhaseModal: 'Diagnostics time phase modal',
  dialog: 'Dialog',
  room: 'Room panel',
  otherRoomsPanel: 'Other rooms panel',
  otherRooms: 'Other rooms - commissioning overview',
  search: 'Search page',
  settings: 'Settings page',
  consumptionChart: 'Consumption chart',
  plantOverview: 'Plant overview link',
  heatingCurveConfirmationModal: 'Heating curve confirmation modal',
  installationDetails: 'Installation details',
  vddBackups: 'Vdd Backups',
  noteModal: 'Note modal',
  migrateInstallation: 'Migrate installations modal',
  gatewayCommissioning: 'Gateway commissioning',
  gatewayCommissioningResidentialType: 'Gateway commissioning residential type',
  gatewayCommissioningCommercialType: 'Gateway commissioning commercial type',
  gatewayCommissioningCheckLight: 'Gateway commissioning check light',
  gatewayCommissioningGwNetwork: 'Gateway commissioning gateway network',
  gatewayCommissioningSerialForm: 'Gateway commissioning serial form',
  gatewayCommissioningEmailForm: 'Gateway commissioning email form',
  gatewayCommissioningOMAForm: 'Gateway commissioning OMA form',
  gatewayCommissioningAddressForm: 'Gateway commissioning address form',
  gatewayCommissioningTransferSuccess: 'Gateway commissioning transfer success',
  gatewayCommissioningOMATransferSuccess: 'Gateway commissioning OMA transfer success',
  gatewayCommissioningWifiInstructions: 'Gateway commissioning wifi instructions',
  energyCockpit: 'Energy cockpit panel',
  energyFlows: 'Energy flows side panel',
  trending: 'Trending',
  transferInstallationDialog: 'Transfer installation dialog',
  energyHistoricalDataModal: 'Energy historical data modal',
  installationDetailsMenu: 'Installation details menu',
  bookingOverview: 'Booking overview',
  hydraulicSchematic: 'Hydraulic schematic',
  bookingSummary: 'Booking summary',
  cascadesLagDevice: 'Cascades Lag device',
  cascadesSequenceStrategy: 'Cascades sequence strategy',
  hems: 'Hems',
  serviceRequest: 'New service request',
  optimizationRecommendation: 'Optimization recommendation',
  pdfViewer: 'Pdf viewer',
};

export const GTM_EVENTS: { [key: string]: GTMEvent } = {
  // GENERIC
  onSave: {
    id: 'gtm_save-btn',
    action: '[SAVE] ',
    category: GTM_CATEGORIES.dialog,
  },
  onConfirm: {
    id: 'gtm_confirm-btn',
    action: '[CONFIRM] ',
    category: GTM_CATEGORIES.dialog,
  },
  onCancel: {
    id: 'gtm_cancel-btn',
    action: '[CANCEL] ',
    category: null,
  },
  onClose: {
    id: 'gtm_close-btn',
    action: '[CLOSE] ',
    category: null,
  },
  onBackDialog: {
    id: 'gtm_back-dialog-btn',
    action: '[BACK] Dialog',
    category: GTM_CATEGORIES.dialog,
  },
  onCancelDialog: {
    id: 'gtm_cancel-dialog-btn',
    action: '[CANCEL] Dialog',
    category: GTM_CATEGORIES.dialog,
  },
  onTryAgain: {
    id: 'gtm_try-again-btn',
    action: '[TRY AGAIN] ',
    category: null,
  },
  onNextStep: {
    id: 'gtm_next-step-btn',
    action: '[GO TO] Next step: ',
    category: GTM_CATEGORIES.dialog,
  },
  onPreviousStep: {
    id: 'gtm_previous-step-btn',
    action: '[GO TO] Previous step: ',
    category: GTM_CATEGORIES.dialog,
  },
  onNextPage: {
    id: 'gtm_next-page-btn',
    action: '[GO TO] Next page: ',
    category: GTM_CATEGORIES.pdfViewer,
  },
  onPreviousPage: {
    id: 'gtm_previous-page-btn',
    action: '[GO TO] Previous page: ',
    category: GTM_CATEGORIES.pdfViewer,
  },
  onZoomModeToggle: {
    id: 'gtm_zoom-mode-toggle-btn',
    action: '[TOGGLE] Zoom mode: ',
    category: GTM_CATEGORIES.pdfViewer,
  },
  onFocusTextField: {
    id: 'gtm_text-field-focus',
    action: '[FOCUS] Text field: ',
    category: null,
  },
  onBlurTextField: {
    id: 'gtm_text-field-blur',
    action: '[BLUR] Text field: ',
    category: null,
  },
  onEditTextField: {
    id: 'gtm_text-field-edit',
    action: '[EDIT] Text field: ',
    category: null,
  },
  onTextField: {
    id: 'gtm_text-field',
    action: 'Text field: ',
    category: null,
  },
  onNumberInput: {
    id: 'gtm_number-input',
    action: 'Number input: ',
    category: null,
  },
  onCloseWriteErrorModal: {
    id: 'gtm_on-close-write-error-modal',
    action: 'Close VDD write error modal: ',
    category: null,
  },
  onEditBtn: {
    id: 'gtm_edit-btn',
    action: '[EDIT] ',
    category: null,
  },
  onPanelExpand: {
    id: 'gtm_expand-panel',
    action: '[EXPAND] ',
    category: null,
  },
  onPanelCollapse: {
    id: 'gtm_collapse-panel',
    action: '[COLLAPSE] ',
    category: null,
  },
  onGoToPage: {
    id: 'gtm_go-to',
    action: '[GO TO] ',
    category: null,
  },
  onGoToShop: {
    id: 'gtm_on-go-to-shop',
    action: '[GO TO] ',
    category: GTM_CATEGORIES.booking,
  },
  onSelectDevice: {
    id: 'gtm_device-select',
    action: '[DEVICE SELECT] ',
    category: null,
  },
  onSelectDiagnosticTab: {
    id: 'gtm_diagnostic-tab-select',
    action: '[DIAGNOSTIC TAB SELECT] ',
    category: null,
  },
  onSelectUpdateTab: {
    id: 'gtm_update-tab-select',
    action: '[UPDATE TAB SELECT] ',
    category: null,
  },
  onRemoteServiceOverlay: {
    id: 'gtm_remote-service-overlay',
    action: '[REMOTE SERVICE OVERLAY] ',
    category: null,
  },
  onSelectRemoteServiceTab: {
    id: 'gtm_remote-service-tab-select',
    action: '[REMOTE SERVICE TAB SELECT] ',
    category: null,
  },
  onSelectExtendedParametersTab: {
    id: 'gtm_extended-parameters-tab-select',
    action: '[EXTENDED PARAMETERS TAB SELECT] ',
    category: null,
  },
  onSelectUseCaseGroup: {
    id: 'gtm_use-case-group-select',
    action: '[USE CASE GROUP SELECT] ',
    category: null,
  },
  onUnselectUseCaseGroup: {
    id: 'gtm_use-case-group-unselect',
    action: '[USE CASE GROUP UNSELECT] ',
    category: null,
  },
  onSelectUseCaseType: {
    id: 'gtm_use-case-type-select',
    action: '[USE CASE TYPE SELECT] ',
    category: null,
  },
  onUnselectUseCaseType: {
    id: 'gtm_use-case-type-unselect',
    action: '[USE CASE TYPE UNSELECT] ',
    category: null,
  },
  onOpen: {
    id: 'gtm_open',
    action: '[OPEN] ',
    category: null,
  },
  onDelete: {
    id: 'gtm_delete',
    action: '[DELETE] ',
    category: null,
  },
  onRedirect: {
    id: 'gtm_redirect',
    action: '[REDIRECT TO] ',
    category: null,
  },
  onReload: {
    id: 'gtm_reload',
    action: '[RELOAD] ',
    category: null,
  },
  onToggle: {
    id: 'gtm_toggle',
    action: '[TOGGLE] ',
    category: null,
  },
  onChange: {
    id: 'gtm_change',
    action: '[CHANGE] ',
    category: null,
  },
  onSelect: {
    id: 'gtm_select',
    action: '[SELECT] ',
    category: null,
  },
  onStart: {
    id: 'gtm_start-btn',
    action: '[START] ',
    category: null,
  },
  onStop: {
    id: 'gtm_stop-btn',
    action: '[STOP] ',
    category: null,
  },
  onClear: {
    id: 'gtm_clear-btn',
    action: '[CLEAR] ',
    category: null,
  },
  onSet: {
    id: 'gtm_set',
    action: '[SET] ',
    category: null,
  },
  onSetFilter: {
    id: 'gtm_filter-chip',
    action: '[SET FILTER] ',
    category: null,
  },
  onRequestSend: {
    id: 'gtm_send-request-btn',
    action: '[SEND REQUEST] ',
    category: null,
  },
  onSearch: {
    id: 'gtm_search',
    action: '[SEARCH] ',
    category: null,
  },
  onClearSearch: {
    id: 'gtm_search-clear',
    action: '[CLEAR] ',
    category: null,
  },
  onActivate: {
    id: 'gtm_activate-btn',
    action: '[ACTIVATE]',
    category: null,
  },
  onDeactivate: {
    id: 'gtm_deactivate-btn',
    action: '[DEACTIVATE]',
    category: null,
  },
  onDownload: {
    id: 'gtm_download-btn',
    action: '[DOWNLOAD] ',
    category: null,
  },
  onExport: {
    id: 'gtm_export-btn',
    action: '[EXPORT] ',
    category: null,
  },

  onAddOtherRoom: {
    id: 'gtm_add-other-rooms',
    action: '[ADD] ',
    category: GTM_CATEGORIES.otherRooms,
  },

  onOptimizationOverlayOpen: {
    id: 'gtm_optimization-recommendation-overlay',
    action: '[OPTIMIZATION RECOMMENDATION OVERLAY] Open optimization overlay',
    category: GTM_CATEGORIES.optimizationRecommendation,
  },

  // Navbar
  onAuthentication: {
    id: 'gtm_user-btn',
    action: '[AUTHENTICATION] To auth process',
    category: GTM_CATEGORIES.navbar,
  },
  onLogout: {
    id: 'gtm_logout-btn',
    action: '[AUTHENTICATION] Logout',
    category: GTM_CATEGORIES.navbar,
  },

  // Breadcrumb
  onBreadcrumb: {
    id: 'gtm_breadcrumb-link',
    action: '[GO BACK] On breadcrumb ',
    category: GTM_CATEGORIES.breadcrumb,
  },
  onBreadcrumbBack: {
    id: 'gtm_breadcrumb-back-arrow',
    action: '[GO BACK] Arrow back',
    category: GTM_CATEGORIES.breadcrumb,
  },

  // Map
  onMapInstallationSelect: {
    id: 'gtm_map-installation-list-item-select',
    action: '[SHOW] Selected installation details',
    label: GTM_LABELS.map,
    category: GTM_CATEGORIES.map,
  },

  // Home Page
  onRegister: {
    id: 'gtm_register-btn',
    action: '[REGISTER] Redirect to Centralized Customer Registration',
    label: GTM_LABELS.home,
    category: GTM_CATEGORIES.landingPage,
  },
  onDemoRun: {
    id: 'gtm_run-demo-btn',
    action: '[GO TO] Demo mode',
    label: GTM_LABELS.home,
    category: GTM_CATEGORIES.landingPage,
  },

  // Search Page
  invokeHistorySearch: {
    id: 'gtm_search-history-search-invoke-item',
    action: '[INVOKE] Search from search history item',
    category: GTM_CATEGORIES.search,
  },

  // BrowserSupport
  onBrowserSelect: {
    id: 'gtm_browser_link',
    action: '[REDIRECT TO] Browser`s website',
    category: GTM_CATEGORIES.browserSupport,
  },

  // Installations page
  onLimitedSearchUnderstand: {
    id: 'gtm_limited-search-info-close',
    action: '[UNDERSTAND] Limited search',
    label: GTM_LABELS.installations,
    category: GTM_CATEGORIES.overview,
  },
  onSort: {
    id: 'gtm_sort-installations',
    action: '[SORT] Installations',
    label: GTM_LABELS.installations,
    category: GTM_CATEGORIES.overview,
  },
  onPage: {
    id: 'gtm_page-installations',
    action: '[PAGE] Installations',
    label: GTM_LABELS.installations,
    category: GTM_CATEGORIES.overview,
  },
  onSize: {
    id: 'gtm_size-installations',
    action: '[SIZE] Installations',
    label: GTM_LABELS.installations,
    category: GTM_CATEGORIES.overview,
  },

  // Installation Details
  onControlShow: {
    id: 'gtm_show-control-btn',
    action: '[SHOW] Control panel',
    label: GTM_LABELS.installationDetails,
    category: GTM_CATEGORIES.controlPanel,
  },
  onActiveErrorsPage: {
    id: 'gtm_active-errors-page',
    action: '[PAGE] Active errors',
    label: GTM_LABELS.installationDetails,
    category: GTM_CATEGORIES.statusPanel,
  },
  rebootGateway: {
    id: 'gtm_gateway-reboot-btn',
    action: '[REBOOT] Gateway',
    label: GTM_LABELS.installationDetails,
    category: GTM_CATEGORIES.gateway,
  },
  rebootDevice: {
    id: 'gtm_device-reboot-btn',
    action: '[REBOOT] Device',
    label: GTM_LABELS.installationDetails,
    category: GTM_CATEGORIES.system,
  },
  resetServiceDate: {
    id: 'gtm_reset-service-date-btn',
    action: '[RESET] Service date',
    label: GTM_LABELS.installationDetails,
    category: GTM_CATEGORIES.system,
  },
  onInstallationDeregister: {
    id: 'gtm_deregister-installation-btn',
    action: '[DEREGISTER] Installation',
    label: GTM_LABELS.installationDetails,
    category: GTM_CATEGORIES.deregisterInstallation,
  },

  // Commissioning
  onUnassignDevice: {
    id: 'gtm_on-unassign-device-btn',
    action: '[UNASSIGN] Device from room',
    category: GTM_CATEGORIES.devicePanel,
  },
  onDeleteDevice: {
    id: 'gtm_on-delete-room-btn',
    action: '[DELETE] Device from room',
    category: GTM_CATEGORIES.deleteDevice,
  },
  onDeleteRoom: {
    id: 'gtm_on-delete-device-btn',
    action: '[DELETE] Room',
    category: GTM_CATEGORIES.deleteRoom,
  },
  onAddDeviceToRoom: {
    id: 'gtm_on-add-device-to-room-btn',
    action: '[ADD] Device to the room',
    category: GTM_CATEGORIES.manageRoom,
  },
  onCameraSwitch: {
    id: 'gtm_on-camera-switch-btn',
    action: '[SWITCH] Camera on device',
    category: GTM_CATEGORIES.barcodeScanner,
  },
  // Manager Panel
  onOnOneTimeCharge: {
    action: '[SWITCH ON] One time charge',
    label: GTM_LABELS.managerPanel,
    category: GTM_CATEGORIES.heatingSchedule,
  },
  onOffOneTimeCharge: {
    action: '[SWITCH OFF] One time charge',
    label: GTM_LABELS.managerPanel,
    category: GTM_CATEGORIES.heatingSchedule,
  },
  onOnEcoMode: {
    action: '[SWITCH ON] Eco mode',
    label: GTM_LABELS.managerPanel,
    category: GTM_CATEGORIES.heatingSchedule,
  },
  onOffEcoMode: {
    action: '[SWITCH OFF] Eco mode',
    label: GTM_LABELS.managerPanel,
    category: GTM_CATEGORIES.heatingSchedule,
  },
  onOnComfortMode: {
    action: '[SWITCH ON] Comfort mode',
    label: GTM_LABELS.managerPanel,
    category: GTM_CATEGORIES.heatingSchedule,
  },
  onOffComfortMode: {
    action: '[SWITCH OFF] Comfort mode',
    label: GTM_LABELS.managerPanel,
    category: GTM_CATEGORIES.heatingSchedule,
  },
  onContinueAfterDaysSelection: {
    id: 'gtm_continue-after-days-selection-btn',
    action: '[CONTINUE] Multiple days selection dialog',
    label: GTM_LABELS.managerPanel,
    category: GTM_CATEGORIES.editSchedule,
  },
  onProgramRemove: {
    id: 'gtm_program-remove',
    action: '[REMOVE] Program',
    label: GTM_LABELS.managerPanel,
    category: GTM_CATEGORIES.editSchedule,
  },
  onAddProgram: {
    id: 'gtm_program-add',
    action: '[NEW] Program',
    label: GTM_LABELS.managerPanel,
    category: GTM_CATEGORIES.editSchedule,
  },
  onProgramUndo: {
    id: 'gtm_program-undo',
    action: '[UNDO] Program',
    label: GTM_LABELS.managerPanel,
    category: GTM_CATEGORIES.editSchedule,
  },
  onProgramApply: {
    id: 'gtm_program-apply',
    action: '[APPLY] Program',
    label: GTM_LABELS.managerPanel,
    category: GTM_CATEGORIES.editSchedule,
  },

  // FORM New invitation
  acceptInvitation: {
    id: 'gtm_accept-invitation-btn',
    action: '[ACCEPT] Invitation',
    label: GTM_LABELS.confirmDialog,
    category: GTM_CATEGORIES.acceptInvitation,
  },
  // DIALOG Holiday Program
  onActivateHolidayProgramYes: {
    id: 'gtm_confirm-activate-btn',
    action: '[ACTIVATE] Holiday program',
    label: GTM_LABELS.confirmDialog,
    category: GTM_CATEGORIES.holidayProgram,
  },
  onActivateHolidayProgramNo: {
    id: 'gtm_cancel-activate-btn',
    action: '[DO NOT ACTIVATE] Holiday program',
    label: GTM_LABELS.confirmDialog,
    category: GTM_CATEGORIES.holidayProgram,
  },
  onDiagStackInstanceAvailability: {
    id: 'gtm_diagstack-info-btn',
    action: '[CHECK AVAILABILITY] Diagstack instance info',
    label: GTM_LABELS.installationDetails,
    category: GTM_CATEGORIES.diagnosticsModal,
  },

  // Diagnostics - Time Phases - STARTSTOPTEMP
  onAddTimePhase: {
    id: 'gtm_time-phase-add',
    action: '[NEW] Time Phase',
    label: GTM_LABELS.confirmDialog,
    category: GTM_CATEGORIES.diagnosticsTimePhaseModal,
  },
  onRemoveTimePhase: {
    id: 'gtm_time-phase-remove',
    action: '[NEW] Time Phase',
    label: GTM_LABELS.diagnosticsPage,
    category: GTM_CATEGORIES.diagnosticsTimePhaseModal,
  },
};
